import { InputHTMLAttributes, FocusEvent, ChangeEvent, useState } from 'react'
import { ChangeHandler, Noop, RefCallBack } from 'react-hook-form'
import cx from 'classnames'

export interface InputChangeEvent {
  target: EventTarget & (HTMLInputElement | HTMLTextAreaElement)
  type?: string
}

type InputChangeHandler = (...event: InputChangeEvent[]) => void

// Combination of ControllerRenderProps and UseFormRegisterReturn from react-hook-form
export interface FormRegister {
  ref: RefCallBack
  name: string
  value?: string | number
  onChange: ChangeHandler | InputChangeHandler
  onBlur: ChangeHandler | Noop
}

interface InputFieldProps {
  type: 'text' | 'email' | 'number' | 'password'
  errorMessage?: string
  formRegister: FormRegister
  label?: string
  borderBottom?: boolean
  isInvertedColors?: boolean
  children?: React.ReactNode
}

const InputField = ({
  id,
  type,
  value,
  defaultValue = '',
  autoComplete,
  placeholder,
  max,
  maxLength,
  className,
  label,
  errorMessage,
  borderBottom,
  formRegister,
  isInvertedColors,
  children,
}: InputFieldProps & InputHTMLAttributes<HTMLInputElement>) => {
  const isManaged = typeof value !== 'undefined'
  const [currentValue, setCurrentValue] = useState(defaultValue)

  const isEmail = type === 'email'
  const isNumber = type === 'number'

  return (
    <div className={cx('grid', className)}>
      <div className={cx('flex flex-col relative text-left')}>
        {label && (
          <label htmlFor={id} className="text-xs mb-2">
            {label}
          </label>
        )}
        <div
          className={cx('flex justify-between', {
            'border-error': errorMessage,
            'border-input-border': !errorMessage && !isInvertedColors,
            'border-input-inverted-border': !errorMessage && isInvertedColors,
            'border-b': borderBottom,
            'border rounded-[3px]': !borderBottom,
          })}
        >
          <input
            id={id}
            type={type}
            inputMode={isEmail ? 'email' : isNumber ? 'decimal' : 'text'}
            max={max}
            maxLength={maxLength}
            autoComplete={autoComplete}
            ref={formRegister.ref}
            name={formRegister.name}
            value={isManaged ? value : currentValue}
            placeholder={placeholder}
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              formRegister.onBlur(event)
              setCurrentValue(event.target.value)
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              formRegister.onChange(event)
              setCurrentValue(event.target.value)
            }}
            className={cx(
              'relative appearance-none w-full h-full py-3',
              'text-base leading-none',
              {
                inverted: isInvertedColors,
                'px-4': !borderBottom,
                'bg-input-bg text-input-text': !isInvertedColors,
                'bg-input-inverted-bg text-input-inverted-text':
                  isInvertedColors,
              }
            )}
          />
          {children}
        </div>
        {errorMessage && (
          <span role="alert" className="mt-2 text-xs text-error">
            {errorMessage}
          </span>
        )}
      </div>
    </div>
  )
}

export default InputField
